import type {
  DailyCardClaimDto,
  DailyCardsStateDto,
  FirstStepsClaimDto,
  FirstStepsStateDto,
  HappyFallBuyDto,
  HappyFallDropDto,
  HappyFallStateDto,
  LevelBoxOpenDto,
  LevelBoxStateDto,
  RewardWidgetsStateDto,
  WheelSpinDto,
  WheelStateDto,
} from "../api/dto/reward_widgets.dto.js";
import { buyHappyFallBalls, claimDailyCard, claimFirstSteps, debugAddHappyFallBall, dropHappyFall, getRewardWidgets, openLevelBox, spinWheel } from "../api/requests/reward_widgets.requests.js";
import { adoptEconomyFromPlayer } from "../api/player_sync.js";
import type { PlayerDto } from "../api/dto/player.dto.js";
import { AuthSessionStore } from "../api/auth_session.js";
import { StatisticsStorage } from "../storage/statistics_storage.js";

export class RewardWidgetsService
{
  private static state: RewardWidgetsStateDto | null = null;
  private static inflight: Promise<RewardWidgetsStateDto | null> | null = null;
  private static pending_player: PlayerDto | null = null;
  private static owner: number | null = null;
  private static fetched_at = 0;
  private static readonly FIRST_STEPS_KEY = "first_steps_state";
  private static stored_first_steps: { owner: number | null; first_steps: FirstStepsStateDto | null } | null | undefined = undefined;

  private constructor() {}

  public static get(): RewardWidgetsStateDto | null
  {
    return RewardWidgetsService.state;
  }

  public static refresh( max_age_ms = 0 ): Promise<RewardWidgetsStateDto | null>
  {
    if ( RewardWidgetsService.inflight )
      return RewardWidgetsService.inflight;

    const owner = AuthSessionStore.getPlayerId();
    const is_same_player = RewardWidgetsService.owner === owner;
    if ( RewardWidgetsService.state && is_same_player && Date.now() - RewardWidgetsService.fetched_at < max_age_ms )
      return Promise.resolve( RewardWidgetsService.state );

    RewardWidgetsService.inflight = getRewardWidgets()
      .then( state =>
      {
        RewardWidgetsService.state = state;
        RewardWidgetsService.owner = owner;
        RewardWidgetsService.fetched_at = Date.now();
        RewardWidgetsService.storeFirstSteps( owner, state?.first_steps ?? null );
        return state;
      } )
      .catch( e =>
      {
        console.warn( "[RewardWidgets] Failed to load state", e );
        return RewardWidgetsService.state;
      } )
      .finally( () => { RewardWidgetsService.inflight = null; } );

    return RewardWidgetsService.inflight;
  }

  public static nextMs( at: string | null ): number
  {
    return at ? Date.parse( at ) : 0;
  }

  public static dailyCards( now_ms = Date.now() ): DailyCardsStateDto | null
  {
    const cards = RewardWidgetsService.state?.daily_cards;
    if ( !cards )
      return null;

    const reopened = !cards.available && cards.next_available_at !== null && RewardWidgetsService.nextMs( cards.next_available_at ) <= now_ms;
    return reopened ? { ...cards, available: true, next_available_at: null } : cards;
  }

  public static wheel( now_ms = Date.now() ): WheelStateDto | null
  {
    const wheel = RewardWidgetsService.state?.wheel;
    if ( !wheel )
      return null;

    const reopened = !wheel.available && wheel.next_available_at !== null && RewardWidgetsService.nextMs( wheel.next_available_at ) <= now_ms;
    return reopened ? { ...wheel, available: true, spins_used: Math.max( 0, wheel.spins_used - 1 ), next_available_at: null } : wheel;
  }

  public static happyFall( now_ms = Date.now() ): HappyFallStateDto | null
  {
    const fall = RewardWidgetsService.state?.happy_fall;
    if ( !fall )
      return null;

    const refilled = fall.balls_used > 0 && fall.next_available_at !== null && RewardWidgetsService.nextMs( fall.next_available_at ) <= now_ms;
    return refilled ? { ...fall, available: true, balls_used: 0, next_available_at: null } : fall;
  }

  public static happyFallRefillAt( now_ms = Date.now() ): number | null
  {
    const fall = RewardWidgetsService.happyFall( now_ms );
    if ( !fall || !fall.next_available_at || fall.balls_total - fall.balls_used + fall.bonus_balls > 0 )
      return null;

    return RewardWidgetsService.nextMs( fall.next_available_at );
  }

  public static levelBox( level = StatisticsStorage.load().level ): LevelBoxStateDto | null
  {
    const box = RewardWidgetsService.state?.level_box;
    if ( !box )
      return null;

    const claimed_levels = box.next_box_level - box.every_levels;
    const claimed = claimed_levels / box.every_levels;
    const completed = Math.max( 0, level - 1 );
    const earned = Math.floor( completed / box.every_levels );
    return {
      ...box,
      available: earned > claimed,
      pending: Math.max( 0, earned - claimed ),
      levels_done: Math.min( box.every_levels, Math.max( 0, completed - claimed_levels ) ),
    };
  }

  public static async claimDailyCard( card_index: number ): Promise<DailyCardClaimDto>
  {
    const result = await claimDailyCard( card_index );
    const state = RewardWidgetsService.state;
    if ( state )
      state.daily_cards = { ...state.daily_cards, available: false, next_available_at: result.next_available_at };
    RewardWidgetsService.pending_player = result.player;
    return result;
  }

  public static async spinWheel(): Promise<WheelSpinDto>
  {
    const result = await spinWheel();
    const state = RewardWidgetsService.state;
    if ( state )
    {
      state.wheel = {
        ...state.wheel,
        available: result.spins_used < result.spins_total,
        spins_used: result.spins_used,
        spins_total: result.spins_total,
        next_available_at: result.next_available_at,
      };
    }
    RewardWidgetsService.pending_player = result.player;
    return result;
  }

  public static async dropHappyFall(): Promise<HappyFallDropDto>
  {
    const result = await dropHappyFall();
    const state = RewardWidgetsService.state;
    if ( state?.happy_fall )
    {
      state.happy_fall = {
        ...state.happy_fall,
        available: result.balls_used < result.balls_total || result.bonus_balls > 0,
        balls_used: result.balls_used,
        balls_total: result.balls_total,
        bonus_balls: result.bonus_balls,
        next_available_at: result.next_available_at,
      };
    }
    return result;
  }

  public static async buyHappyFallBalls(): Promise<HappyFallBuyDto>
  {
    const result = await buyHappyFallBalls();
    const state = RewardWidgetsService.state;
    if ( state )
      state.happy_fall = result.happy_fall;
    return result;
  }

  public static async debugAddHappyFallBall(): Promise<HappyFallStateDto>
  {
    const { happy_fall } = await debugAddHappyFallBall();
    const state = RewardWidgetsService.state;
    if ( state )
      state.happy_fall = happy_fall;
    return happy_fall;
  }

  public static firstSteps(): FirstStepsStateDto | null
  {
    const state = RewardWidgetsService.state;
    if ( state )
      return state.first_steps ?? null;

    return RewardWidgetsService.storedFirstSteps();
  }

  private static storedFirstSteps(): FirstStepsStateDto | null
  {
    if ( RewardWidgetsService.stored_first_steps === undefined )
    {
      try
      {
        const parsed = JSON.parse( localStorage.getItem( RewardWidgetsService.FIRST_STEPS_KEY ) ?? "null" );
        RewardWidgetsService.stored_first_steps = parsed && typeof parsed === "object" ? parsed : null;
      }
      catch
      {
        RewardWidgetsService.stored_first_steps = null;
      }
    }

    const stored = RewardWidgetsService.stored_first_steps;
    if ( !stored || stored.owner !== AuthSessionStore.getPlayerId() )
      return null;

    return stored.first_steps;
  }

  private static storeFirstSteps( owner: number | null, first_steps: FirstStepsStateDto | null ): void
  {
    RewardWidgetsService.stored_first_steps = { owner, first_steps };
    try
    {
      localStorage.setItem( RewardWidgetsService.FIRST_STEPS_KEY, JSON.stringify( RewardWidgetsService.stored_first_steps ) );
    }
    catch
    {
    }
  }

  public static async claimFirstSteps(): Promise<FirstStepsClaimDto>
  {
    const result = await claimFirstSteps();
    const state = RewardWidgetsService.state;
    if ( state )
      state.first_steps = null;
    RewardWidgetsService.storeFirstSteps( AuthSessionStore.getPlayerId(), null );
    RewardWidgetsService.pending_player = result.player;
    return result;
  }

  public static async openLevelBox(): Promise<LevelBoxOpenDto>
  {
    const result = await openLevelBox();
    const state = RewardWidgetsService.state;
    if ( state )
      state.level_box = result.level_box;
    RewardWidgetsService.pending_player = result.player;
    return result;
  }

  public static applyClaimedRewards(): void
  {
    const player = RewardWidgetsService.pending_player;
    RewardWidgetsService.pending_player = null;
    if ( player )
      adoptEconomyFromPlayer( player );
  }
}
